import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Printer, Loader2, AlertTriangle, FlaskConical, Activity, Bandage, Search, Pill, CheckCircle2,
} from "lucide-react";
import { api, fmtDate } from "../lib/api";
import useEscClose from "../hooks/useEscClose";
import { SEV_MILD, SEV_CONCERN, severityTierChip as severityTier } from "../lib/severityTiers";

const KIND_META = {
  out_of_range:  { icon: <AlertTriangle size={14} className="text-rose-300" />,    title: "Out-of-range markers" },
  deficiency:    { icon: <Activity size={14} className="text-violet-300" />,        title: "Top deficiency drill-down" },
  on_rest:       { icon: <Bandage size={14} className="text-orange-300" />,         title: "Athletes on rest" },
};

// Tier thresholds imported from ../lib/severityTiers to prevent drift with
// BiometricsTrendModal + SquadWeatherMap.
const SEV_MILD_CUTOFF = SEV_MILD;
const SEV_CONCERN_CUTOFF = SEV_CONCERN;

/**
 * Drill-down modal for the three Squad-Overview KPI tiles: Out of Range,
 * Top Deficiency, On Rest. Print button uses CSS `@media print` to layout
 * a clean PDF version that the operator can "Save as PDF" from the
 * browser dialog.
 */
export default function KpiDrilldownModal({ open, onClose, kind, initialMarker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [marker, setMarker] = useState(initialMarker || null);

  useEscClose(open, onClose);

  useEffect(() => {
    if (!open || !kind) return;
    setLoading(true); setError(""); setSearch("");
    const params = { kind };
    if (kind === "deficiency" && marker) params.marker = marker;
    api.get("/overview/drilldown", { params })
      .then((r) => {
        setData(r.data);
        // Sync local marker state with whatever the server picked (default).
        if (kind === "deficiency" && r.data?.marker) setMarker(r.data.marker);
      })
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to load drill-down"))
      .finally(() => setLoading(false));
  }, [open, kind, marker]);

  // Reset marker selection when the modal closes so a re-open starts fresh.
  useEffect(() => { if (!open) setMarker(initialMarker || null); }, [open, initialMarker]);

  const meta = KIND_META[kind] || { icon: null, title: "Drill-down" };

  const items = useMemo(() => {
    if (!data?.items) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter((it) => it.sailor_name.toLowerCase().includes(q));
  }, [data, search]);

  const handlePrint = () => {
    // Tag the body so a print-only stylesheet can scope to this modal and
    // hide the rest of the dashboard chrome.
    document.body.classList.add("kpi-printing");
    setTimeout(() => {
      window.print();
      // Cleanup runs after the print dialog closes; small delay so the
      // class isn't cleared before chrome captures the page.
      setTimeout(() => document.body.classList.remove("kpi-printing"), 1000);
    }, 50);
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-[#040D1A]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-6 px-4 kpi-drilldown-root"
      data-testid="kpi-drilldown-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="card w-full max-w-4xl p-5 md:p-6 kpi-drilldown-card">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
              {meta.icon} Squad overview · drill-down
            </div>
            <div className="font-display text-lg text-slate-100 mt-0.5" data-testid="kpi-drilldown-title">
              {data?.label || meta.title}
              {data?.total != null && (
                <span className="text-slate-400 text-sm font-normal ml-2 font-mono-data">
                  · {data.total} athlete{data.total === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 kpi-drilldown-meta">
              Generated {new Date().toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
          <div className="flex items-center gap-2 kpi-drilldown-actions">
            <button onClick={handlePrint}
                    className="pill active !text-[11px] !py-1 !px-2.5"
                    data-testid="kpi-drilldown-print"
                    title="Open the browser print dialog → choose 'Save as PDF'">
              <Printer size={11} /> Print to PDF
            </button>
            <button onClick={onClose} className="pill !p-1.5"
                    data-testid="kpi-drilldown-close"><X size={14} /></button>
          </div>
        </div>

        {error && (
          <div className="card !bg-rose-500/10 !border-rose-500/30 px-4 py-2.5 text-sm text-rose-200 mb-3">
            {error}
          </div>
        )}

        <div className="relative mb-3 kpi-drilldown-search flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" placeholder="Search athlete…" value={search}
                   onChange={(e) => setSearch(e.target.value)}
                   className="pl-7 pr-3 py-1.5 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm w-full placeholder-slate-500 focus:outline-none focus:border-[#00E5FF]/60"
                   data-testid="kpi-drilldown-search" />
          </div>

          {kind === "deficiency" && data?.available_markers?.length > 0 && (
            <select
              value={marker || data.marker || ""}
              onChange={(e) => setMarker(e.target.value)}
              className="py-1.5 px-2 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-[12px] text-slate-200 focus:outline-none focus:border-[#00E5FF]/60"
              data-testid="kpi-drilldown-marker-switcher"
              title="Switch which deficient marker to drill into"
            >
              {data.available_markers.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label} — {m.count} athlete{m.count === 1 ? "" : "s"} ({m.pct}%)
                </option>
              ))}
            </select>
          )}
        </div>

        {kind === "deficiency" && data && (
          <div
            className="rounded-lg border border-cyan-500/30 bg-cyan-500/[0.06] px-3 py-2 mb-3 text-[11px] font-mono-data text-cyan-200 flex items-center gap-2"
            data-testid="kpi-drilldown-supp-banner"
          >
            <Pill size={11} className="text-cyan-300" />
            {data.on_supplement_count} of {data.total} affected athlete{data.total === 1 ? "" : "s"} already on a {data.marker_label} supplement.
            {data.total - data.on_supplement_count > 0 && (
              <span className="text-amber-200">
                · {data.total - data.on_supplement_count} not yet supplemented
              </span>
            )}
          </div>
        )}

        {loading ? (
          <div className="card p-10 text-center text-slate-500">
            <Loader2 size={14} className="animate-spin inline mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="card p-8 text-center text-slate-500 text-sm">
            {search ? "No athletes match this filter." : "No athletes in this list."}
          </div>
        ) : (
          <div className="card !bg-[#0B1627] p-0 overflow-hidden kpi-drilldown-list">
            <div className="divide-y divide-[#1E304B] max-h-[60vh] overflow-y-auto kpi-drilldown-scroll">
              {items.map((it) => (
                <DrilldownRow key={it.sailor_id} kind={kind} item={it} marker={data?.marker_label} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function DrilldownRow({ kind, item, marker }) {
  return (
    <div className="px-4 py-3 kpi-drilldown-row" data-testid={`kpi-drilldown-row-${item.sailor_id}`}>
      <div className="flex flex-wrap items-baseline gap-2">
        <div className="font-display text-sm text-slate-100">{item.sailor_name}</div>
        <span className="text-[10px] text-slate-500 font-mono-data">{item.sailor_group}</span>
        {item.latest_date && (
          <span className="text-[10px] text-slate-500 font-mono-data">· latest {fmtDate(item.latest_date)}</span>
        )}
        {kind === "out_of_range" && item.severity_score != null && (() => {
          const tier = severityTier(item.severity_score);
          return (
            <span
              className={`ml-auto px-1.5 py-0.5 rounded text-[10px] font-mono-data border ${tier.cls}`}
              title="Weighted clinical-severity score: importance × direction × magnitude. Higher = more concerning."
              data-testid={`kpi-sev-chip-${item.sailor_id}`}
            >
              severity {item.severity_score.toFixed(1)} · {tier.label}
            </span>
          );
        })()}
      </div>

      {kind === "out_of_range" && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {item.markers.map((m) => (
            <span key={m.key}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-mono-data border ${
                    m.tone === "rose"
                      ? "bg-rose-500/10 border-rose-500/40 text-rose-200"
                      : "bg-amber-500/10 border-amber-500/40 text-amber-200"
                  }`}
                  title={m.severity_reason || undefined}>
              {m.label}: <strong>{m.value}</strong> {m.unit}
              <span className="text-slate-400 ml-1">
                (ref {m.low ?? "—"}–{m.high ?? "—"})
              </span>
              {m.severity > 0 && (
                <span
                  className="ml-1 px-1 py-0 rounded text-[9px] bg-black/30 font-semibold"
                  style={{ color: m.severity >= SEV_CONCERN_CUTOFF ? "#FDA4AF" : m.severity >= SEV_MILD_CUTOFF ? "#FCD34D" : "#6EE7B7" }}
                >
                  sev {m.severity.toFixed(1)}
                </span>
              )}
            </span>
          ))}
        </div>
      )}

      {kind === "deficiency" && (
        <>
          <div className="mt-1.5 text-[12px] font-mono-data text-rose-200">
            {marker}: <strong>{item.value}</strong> {item.unit}
            <span className="text-slate-400 ml-1.5">(ref {item.low ?? "—"}–{item.high ?? "—"})</span>
            {item.deficit != null && (
              <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-rose-500/10 border border-rose-500/40 text-rose-200">
                {item.deficit}% below reference
              </span>
            )}
          </div>
          {item.supplements && item.supplements.length > 0 ? (
            <div className="mt-2 ml-3 border-l-2 border-cyan-500/30 pl-3 space-y-1">
              <div className="text-[9px] uppercase tracking-widest text-cyan-300 flex items-center gap-1">
                <Pill size={10} /> Supplements for this deficiency
              </div>
              {item.supplements.map((s) => (
                <div key={s.id} className="text-[11px] font-mono-data text-slate-300 flex flex-wrap items-center gap-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider border ${
                    s.status === "active"    ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-200" :
                    s.status === "completed" ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-200" :
                    s.status === "stopped"   ? "bg-rose-500/10 border-rose-500/40 text-rose-200" :
                                               "bg-amber-500/10 border-amber-500/40 text-amber-200"
                  }`}>{s.status}</span>
                  <span className="text-slate-100">{s.name}</span>
                  {s.dose && <span className="text-slate-500">· {s.dose}</span>}
                  {s.frequency && <span className="text-slate-500">· {s.frequency}</span>}
                  <span className="text-slate-500">· started {s.start_date}{s.end_date ? ` → ${s.end_date}` : ""}</span>
                  {s.adherence_pct != null && (
                    <span className={`ml-1 inline-flex items-center gap-0.5 ${
                      s.adherence_pct >= 80 ? "text-emerald-300" :
                      s.adherence_pct >= 50 ? "text-amber-300" : "text-rose-300"
                    }`}>
                      <CheckCircle2 size={10} /> {s.adherence_taken}/{s.adherence_count} ({s.adherence_pct}%)
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-2 ml-3 text-[10px] italic text-amber-300 flex items-center gap-1">
              <AlertTriangle size={10} /> No {marker} supplement prescribed yet
            </div>
          )}
        </>
      )}

      {kind === "on_rest" && (
        <div className="mt-1.5 flex flex-col gap-1">
          {(item.injuries || []).map((inj) => (
            <div key={inj.id} className="text-[11px] text-slate-300">
              <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono-data border ${
                inj.status === "active"
                  ? "bg-rose-500/15 border-rose-500/40 text-rose-200"
                  : "bg-amber-500/15 border-amber-500/40 text-amber-200"
              }`}>
                {inj.status}
              </span>
              <span className="ml-2 font-mono-data text-[10px] text-slate-500">{fmtDate(inj.onset_date)}</span>
              <span className="ml-2">{inj.diagnosis || inj.body_part_other || inj.body_part || inj.type_other || inj.type}</span>
              {inj.severity && <span className="ml-2 text-slate-500 text-[10px]">· severity: {inj.severity}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
